import { defineStore } from 'pinia';
import { createTopPayOnrampOrder, getTopPayOnrampOrders, getTopPaySupported } from '@/api/toppay';
import {
  persistCheckoutModePreference,
  readPreferredCheckoutMode,
} from '@/utils/deposit-onramp-checkout';
import {
  TOPPAY_ORDER_POLL_MAX_MS,
  TOPPAY_ORDER_POLL_MS,
  findTopPayOrderById,
  isTopPayReturnTerminalPhase,
  normalizeTopPayOrdersPage,
  resolveTopPayReturnPhase,
} from '@/utils/toppay-order-status';
import { isRequestTimeoutLike, withRetry } from '@/utils/request-retry';
import {
  buildTopPayCreateOrderPayload,
  creditUsdToPayFiat,
  fiatToCreditUsd,
} from '@/utils/toppay-amount';
import {
  TOPPAY_REGION_TO_CURRENCY,
  currencyToTopPayRegion,
  stripIndiaFromTopPayRegions,
  topPayAmountDecimals,
} from '@/utils/toppay-region';

/** Minimum USD credit; fiat minimum derived from rate / API. */
export const TOPPAY_DEPOSIT_MIN_USD = 5;
export const TOPPAY_DEPOSIT_DEFAULT_VND = 500000;
export const TOPPAY_DEPOSIT_DEFAULT_BRL = 30;
export const TOPPAY_DEPOSIT_DEFAULT_MXN = 100;
export const TOPPAY_DEPOSIT_DEFAULT_PHP = 300;
export const TOPPAY_DEPOSIT_DEFAULT_INR = 4200;
export const TOPPAY_DEPOSIT_DEFAULT_IDR = 80000;
export const TOPPAY_DEPOSIT_DEFAULT_USD = 5;

let supportedLoadPromise = null;
let returnPollTimer = null;
let returnPollInflight = false;
let returnPollStartedAt = 0;

function readPositiveNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function isHiddenTopPayMethod(method) {
  const code = String(method ?? '').trim().toUpperCase();
  // VN_BANKAPI: legacy hidden rail; SOLANA_USDC: channel retired 2026-08-31
  return code === 'VN_BANKAPI' || code === 'SOLANA_USDC';
}

function visibleTopPayMethods(methods) {
  const list = Array.isArray(methods) ? methods : [];
  return list.filter((method) => !isHiddenTopPayMethod(method));
}

function emptyRegionState(region = 'VN') {
  const r = String(region || 'VN').toUpperCase();
  const currency = TOPPAY_REGION_TO_CURRENCY[r] || 'VND';
  const defaultFiat =
    r === 'BR'
      ? TOPPAY_DEPOSIT_DEFAULT_BRL
      : r === 'MX'
        ? TOPPAY_DEPOSIT_DEFAULT_MXN
        : r === 'PH'
          ? TOPPAY_DEPOSIT_DEFAULT_PHP
          : r === 'IN'
            ? TOPPAY_DEPOSIT_DEFAULT_INR
            : r === 'ID'
              ? TOPPAY_DEPOSIT_DEFAULT_IDR
              : r === 'US'
                ? TOPPAY_DEPOSIT_DEFAULT_USD
                : TOPPAY_DEPOSIT_DEFAULT_VND;
  return {
    enabled: false,
    fiatPerUsd:
      r === 'BR'
        ? '5.80'
        : r === 'MX'
          ? '18.50'
          : r === 'PH'
            ? '58.00'
            : r === 'IN'
              ? '84.00'
              : r === 'ID'
                ? '16000.00'
                : r === 'US'
                  ? '1.00'
                  : '25000',
    minPayMoney: String(defaultFiat),
    maxPayMoney: '',
    methods: [],
    currency,
  };
}

function parseSupportedRegions(res) {
  const regions = {
    VN: emptyRegionState('VN'),
    BR: emptyRegionState('BR'),
    MX: emptyRegionState('MX'),
    PH: emptyRegionState('PH'),
    IN: emptyRegionState('IN'),
    ID: emptyRegionState('ID'),
    US: emptyRegionState('US'),
  };
  const fromApi = res?.regions && typeof res.regions === 'object' ? res.regions : null;
  if (fromApi) {
    for (const key of ['VN', 'BR', 'MX', 'PH', 'IN', 'ID', 'US']) {
      const row = fromApi[key];
      if (!row?.enabled) continue;
      const currency = String(row.currency || TOPPAY_REGION_TO_CURRENCY[key]).toUpperCase();
      const rate = String(
        row.fiatPerUsd ||
          row.vndPerUsd ||
          row.brlPerUsd ||
          row.mxnPerUsd ||
          row.phpPerUsd ||
          row.inrPerUsd ||
          row.idrPerUsd ||
          regions[key].fiatPerUsd
      );
      const min = String(row.minPayMoney || row.minPayMoneyVnd || regions[key].minPayMoney);
      regions[key] = {
        enabled: true,
        fiatPerUsd: rate,
        minPayMoney: min,
        maxPayMoney: String(row.maxPayMoney || ''),
        amountDecimals: Number.isInteger(row.amountDecimals)
          ? row.amountDecimals
          : topPayAmountDecimals(key),
        provider: String(row.provider || (key === 'ID' ? 'PAYING' : 'TOPPAY')),
        methods: visibleTopPayMethods(row.methods),
        currency,
      };
    }
    return stripIndiaFromTopPayRegions(regions);
  }
  if (res?.enabled) {
    regions.VN = {
      enabled: true,
      fiatPerUsd: String(res.vndPerUsd || '25000'),
      minPayMoney: String(res.minPayMoneyVnd || TOPPAY_DEPOSIT_MIN_USD * 25000),
      methods: visibleTopPayMethods(res.methods),
      currency: 'VND',
    };
  }
  return stripIndiaFromTopPayRegions(regions);
}

export const useTopPayDepositStore = defineStore('toppayDeposit', {
  state: () => ({
    activeRegion: 'VN',
    regions: {
      VN: emptyRegionState('VN'),
      BR: emptyRegionState('BR'),
      MX: emptyRegionState('MX'),
      PH: emptyRegionState('PH'),
      IN: emptyRegionState('IN'),
      ID: emptyRegionState('ID'),
      US: emptyRegionState('US'),
    },
    /** @deprecated VN-only compat */
    enabled: false,
    vndPerUsd: '25000',
    minPayMoneyVnd: String(TOPPAY_DEPOSIT_MIN_USD * 25000),
    methods: [],
    listLoading: false,
    listFetchError: '',
    selectedMethod: 'BANK_QR',
    amount: '',
    orderCreditUsd: 0,
    orderCreating: false,
    orderError: '',
    checkoutUrl: '',
    orderId: '',
    returnedAt: null,
    returnPhase: '',
    returnStatus: '',
    checkoutMode: 'iframe',
    popupBlocked: false,
  }),

  getters: {
    activeConfig(state) {
      const r = String(state.activeRegion || 'VN').toUpperCase();
      return state.regions[r] || emptyRegionState(r);
    },
    activeCurrency(state) {
      return this.activeConfig.currency || TOPPAY_REGION_TO_CURRENCY[state.activeRegion] || 'VND';
    },
    amountNum(state) {
      return readPositiveNumber(state.amount);
    },
    fiatPerUsdNum(state) {
      const fromCfg = readPositiveNumber(this.activeConfig.fiatPerUsd);
      if (fromCfg > 0) return fromCfg;
      const r = String(state.activeRegion || 'VN').toUpperCase();
      if (r === 'US') return 1;
      if (r === 'BR') return 5.8;
      if (r === 'MX') return 18.5;
      if (r === 'PH') return 58;
      if (r === 'IN') return 84;
      if (r === 'ID') return 16000;
      return readPositiveNumber(state.vndPerUsd) || 25000;
    },
    amountDecimals(state) {
      return topPayAmountDecimals(state.activeRegion);
    },
    minPayMoneyNum(state) {
      const fromApi = readPositiveNumber(this.activeConfig.minPayMoney);
      if (fromApi > 0) return fromApi;
      const rate = this.fiatPerUsdNum;
      return creditUsdToPayFiat(TOPPAY_DEPOSIT_MIN_USD, rate, state.activeRegion);
    },
    maxPayMoneyNum() {
      return readPositiveNumber(this.activeConfig.maxPayMoney);
    },
    minPayMoneyVndNum(state) {
      if (String(state.activeRegion).toUpperCase() !== 'VN') return 0;
      return Math.ceil(this.minPayMoneyNum);
    },
    depositMin() {
      return this.minPayMoneyNum;
    },
    payMoneyFiatDisplay(state) {
      const decimals = this.amountDecimals;
      const n = this.amountNum;
      if (!n) return '';
      return decimals === 0 ? String(Math.floor(n)) : n.toFixed(2);
    },
    payMoneyVndDisplay(state) {
      if (String(state.activeRegion).toUpperCase() !== 'VN') return this.payMoneyFiatDisplay;
      return this.payMoneyFiatDisplay;
    },
    creditUsdDisplay(state) {
      const usd = fiatToCreditUsd(state.amount, this.activeConfig.fiatPerUsd, this.amountDecimals);
      if (!usd) return '';
      return (Math.floor(usd * 100) / 100).toFixed(2);
    },
    creditUsdNum() {
      return readPositiveNumber(this.creditUsdDisplay);
    },
    creditedUsdNum(state) {
      const fromOrder = Number(state.orderCreditUsd);
      if (Number.isFinite(fromOrder) && fromOrder > 0) return fromOrder;
      return fiatToCreditUsd(state.amount, this.activeConfig.fiatPerUsd, this.amountDecimals);
    },
    regionEnabled(state) {
      return (currency) => {
        const region = currencyToTopPayRegion(currency);
        if (!region) return false;
        return Boolean(state.regions[region]?.enabled);
      };
    },
  },

  actions: {
    setActiveRegion(region) {
      const r =
        String(region ?? 'VN')
          .trim()
          .toUpperCase() || 'VN';
      this.activeRegion = r;
      const cfg = this.regions[r] || emptyRegionState(r);
      this.methods = [...(cfg.methods || [])];
      this.vndPerUsd = r === 'VN' ? cfg.fiatPerUsd : this.vndPerUsd;
      this.enabled = Boolean(cfg.enabled) || Object.values(this.regions).some((x) => x.enabled);
      if (this.methods.length && !this.methods.includes(this.selectedMethod)) {
        this.selectedMethod = this.methods[0];
      }
      if (!this.methods.length) {
        this.selectedMethod = '';
      }
    },

    setActiveRegionByCurrency(currency) {
      const region = currencyToTopPayRegion(currency);
      if (region && this.regions[region]?.enabled) {
        this.setActiveRegion(region);
      }
    },

    setAmount(value) {
      this.amount = String(value ?? '');
    },

    setSelectedMethod(method) {
      this.selectedMethod = String(method ?? '').trim() || this.methods[0] || 'BANK_QR';
    },

    bootstrapAmountStep({ reset = false } = {}) {
      if (reset || !this.amount.trim()) {
        this.amount = String(this.minPayMoneyNum || this.defaultAmountForRegion());
      }
    },

    defaultAmountForRegion() {
      const r = String(this.activeRegion).toUpperCase();
      if (r === 'BR') return TOPPAY_DEPOSIT_DEFAULT_BRL;
      if (r === 'MX') return TOPPAY_DEPOSIT_DEFAULT_MXN;
      if (r === 'PH') return TOPPAY_DEPOSIT_DEFAULT_PHP;
      if (r === 'IN') return TOPPAY_DEPOSIT_DEFAULT_INR;
      if (r === 'ID') return TOPPAY_DEPOSIT_DEFAULT_IDR;
      if (r === 'US') return TOPPAY_DEPOSIT_DEFAULT_USD;
      return TOPPAY_DEPOSIT_DEFAULT_VND;
    },

    async loadSupported() {
      if (supportedLoadPromise) {
        await supportedLoadPromise;
        return;
      }
      this.listLoading = true;
      this.listFetchError = '';
      supportedLoadPromise = (async () => {
        try {
          const res = await withRetry(() => getTopPaySupported(), { retries: 2, delayMs: 800 });
          this.regions = parseSupportedRegions(res);
          this.enabled = Object.values(this.regions).some((r) => r.enabled);
          const vn = this.regions.VN;
          if (vn?.enabled) {
            this.vndPerUsd = String(vn.fiatPerUsd);
            this.minPayMoneyVnd = String(vn.minPayMoney);
          }
          this.setActiveRegion(this.activeRegion);
        } catch (err) {
          this.enabled = false;
          this.regions = {
            VN: emptyRegionState('VN'),
            BR: emptyRegionState('BR'),
            MX: emptyRegionState('MX'),
            PH: emptyRegionState('PH'),
            IN: emptyRegionState('IN'),
            ID: emptyRegionState('ID'),
            US: emptyRegionState('US'),
          };
          this.methods = [];
          if (!isRequestTimeoutLike(err)) {
            this.listFetchError = err?.message || 'deposit.cashMethodsEmpty';
          }
        } finally {
          this.listLoading = false;
          supportedLoadPromise = null;
        }
      })();
      await supportedLoadPromise;
    },

    async createOnrampOrder(options = {}) {
      if (this.orderCreating) return null;

      const region = String(options.region || this.activeRegion || 'VN').toUpperCase();
      const cfg = this.regions[region] || emptyRegionState(region);
      const decimals = topPayAmountDecimals(region);

      let payFiat = Number(options.payMoney ?? options.payMoneyVnd);
      if (!Number.isFinite(payFiat) || payFiat <= 0) {
        payFiat = decimals === 0 ? Math.floor(this.amountNum) : Number(this.amountNum.toFixed(2));
      }

      let usd = Number(options.creditUsd);
      if (!Number.isFinite(usd) || usd <= 0) {
        usd = fiatToCreditUsd(payFiat, cfg.fiatPerUsd, decimals);
      }

      const minPay =
        readPositiveNumber(cfg.minPayMoney) ||
        creditUsdToPayFiat(TOPPAY_DEPOSIT_MIN_USD, cfg.fiatPerUsd, region);
      const maxPay = readPositiveNumber(cfg.maxPayMoney);
      if (region === 'ID' && !Number.isInteger(payFiat)) {
        this.orderError = 'deposit.payingAmountInteger';
        return null;
      }
      if (payFiat < minPay) {
        this.orderError =
          region === 'ID' ? 'deposit.payingAmountBelowMin' : 'deposit.toppayAmountBelowMin';
        return null;
      }
      if (maxPay > 0 && payFiat > maxPay) {
        this.orderError = 'deposit.payingAmountAboveMax';
        return null;
      }
      if (usd < TOPPAY_DEPOSIT_MIN_USD && region !== 'ID') return null;

      this.orderCreating = true;
      this.orderError = '';
      this.checkoutUrl = '';
      this.orderId = '';
      this.orderCreditUsd = usd;
      this.activeRegion = region;

      try {
        const res = await createTopPayOnrampOrder(
          buildTopPayCreateOrderPayload({
            region,
            creditUsd: usd,
            method: options.method || this.selectedMethod,
            payMoney: payFiat,
          })
        );
        const checkoutUrl = String(res?.checkoutUrl || '').trim();
        if (!checkoutUrl) {
          this.orderError =
            region === 'ID'
              ? 'deposit.payingCheckoutUnavailable'
              : 'deposit.cashMethodsEmpty';
          this.orderCreditUsd = 0;
          return null;
        }
        this.checkoutUrl = checkoutUrl;
        this.orderId = String(res?.merchantOrderNo ?? res?.id ?? '').trim();
        return checkoutUrl;
      } catch (err) {
        this.orderError =
          err?.message ||
          (region === 'ID' ? 'deposit.payingCheckoutUnavailable' : 'deposit.cashMethodsEmpty');
        this.orderCreditUsd = 0;
        return null;
      } finally {
        this.orderCreating = false;
      }
    },

    stopReturnStatusPoll() {
      if (returnPollTimer != null) {
        clearInterval(returnPollTimer);
        returnPollTimer = null;
      }
      returnPollInflight = false;
      returnPollStartedAt = 0;
    },

    async tickReturnStatusPoll() {
      if (returnPollInflight) return;
      if (!this.orderId && !this.returnedAt) return;
      const timedOut = Date.now() - returnPollStartedAt >= TOPPAY_ORDER_POLL_MAX_MS;
      returnPollInflight = true;
      try {
        const res = await getTopPayOnrampOrders({ pageNum: 1, pageSize: 5 });
        const { rows } = normalizeTopPayOrdersPage(res);
        const order = this.orderId ? findTopPayOrderById(rows, this.orderId) : rows[0] ?? null;
        if (order) {
          this.returnStatus = String(order.status || '').trim();
          this.returnPhase = resolveTopPayReturnPhase(order.status, {
            timedOut: false,
            ledgerCredited: Boolean(order.ledgerCredited),
          });
          if (isTopPayReturnTerminalPhase(this.returnPhase)) {
            this.stopReturnStatusPoll();
          }
          return;
        }
        if (timedOut) {
          this.returnPhase = 'neutral';
          this.stopReturnStatusPoll();
        } else if (!this.returnPhase) {
          this.returnPhase = 'pending';
        }
      } catch (err) {
        console.warn('[toppay-deposit] poll failed', err);
        if (timedOut) {
          this.returnPhase = 'neutral';
          this.stopReturnStatusPoll();
        }
      } finally {
        returnPollInflight = false;
      }
    },

    startOrderStatusPoll() {
      this.stopReturnStatusPoll();
      this.returnPhase = 'pending';
      this.returnStatus = '';
      returnPollStartedAt = Date.now();
      returnPollTimer = setInterval(() => {
        void this.tickReturnStatusPoll();
      }, TOPPAY_ORDER_POLL_MS);
      void this.tickReturnStatusPoll();
    },

    markReturned() {
      this.returnedAt = Date.now();
      this.startOrderStatusPoll();
    },

    initCheckoutModeFromPreference() {
      this.checkoutMode = readPreferredCheckoutMode();
      this.popupBlocked = false;
    },

    setCheckoutMode(mode, options = {}) {
      const next = mode === 'popup' ? 'popup' : 'iframe';
      this.checkoutMode = next;
      if (next === 'iframe') this.popupBlocked = false;
      if (options.persist === false) return;
      persistCheckoutModePreference(next);
    },

    markPopupBlocked(blocked) {
      this.popupBlocked = Boolean(blocked);
    },

    resetCheckoutSurface() {
      this.checkoutUrl = '';
      this.orderError = '';
      this.popupBlocked = false;
      this.orderCreditUsd = 0;
    },

    clearReturnStatus() {
      this.stopReturnStatusPoll();
      this.returnedAt = null;
      this.returnPhase = '';
      this.returnStatus = '';
      this.orderCreditUsd = 0;
    },
  },
});
